/**
 * Panel de Monitoreo de Casos Epidemiológicos para Administradores.
 * Permite la visualización global de todos los casos registrados en la plataforma,
 * con herramientas avanzadas de filtrado por laboratorio, municipio, parásito y nivel de riesgo.
 */

import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Apiurl } from "../../shared/services/apirest";
import AdminLayout from "./AdminLayout";
import bootstrap from "bootstrap/dist/js/bootstrap.bundle";

const ParasiteIcon = () => null; // Icono personalizado removido por ahora

const AdminCases = () => {
    const [casos, setCasos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [labFilter, setLabFilter] = useState("all");
    const [filterMunicipio, setFilterMunicipio] = useState("");
    const [filterParasito, setFilterParasito] = useState("");
    const [riskFilter, setRiskFilter] = useState("all");
    const [originFilter, setOriginFilter] = useState("all");
    const [sortOrder, setSortOrder] = useState("asc");
    const [laboratorios, setLaboratorios] = useState([]);
    const [currentUserRole, setCurrentUserRole] = useState("");
    const toastRef = useRef(null);

    /**
     * Gestiona la exportación de la base de datos de casos en diferentes formatos científicos y administrativos.
     */
    const handleExport = async (format) => {
        const token = localStorage.getItem("token");
        if (!token) return;

        showToast(`⏳ Preparando descarga en formato ${format.toUpperCase()}...`, "info");

        try {
            const response = await axios.get(`${Apiurl}casos/export/?export_type=${format}&resource=casos`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            const extensions = { csv: 'csv', json: 'json', geojson: 'geojson', pdf: 'pdf', xlsx: 'xlsx' };
            link.setAttribute('download', `export_casos_geohcx_${new Date().getTime()}.${extensions[format]}`);

            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);

            showToast(`✅ Archivo ${format.toUpperCase()} descargado con éxito.`, "success");
        } catch (err) {
            console.error("Error al exportar:", err);
            showToast("❌ Error al generar la exportación.", "danger");
        }
    };

    // Lógica de filtrado y ordenamiento memorizada para optimizar el rendimiento
    const filteredCasos = React.useMemo(() => {
        let result = [...casos];

        // 1. Búsqueda por texto (Nombre del paciente, municipio o nivel de peligro)
        if (filter) {
            const term = filter.toLowerCase();
            result = result.filter(c =>
                (c.paciente_nombre || "").toLowerCase().includes(term) ||
                (c.paciente_municipio || "").toLowerCase().includes(term) ||
                (c.nivel_peligrosidad || "").toLowerCase().includes(term)
            );
        }

        // 2. Filtro por Laboratorio
        if (labFilter !== "all") {
            result = result.filter(c => c.laboratorio_nombre === labFilter);
        }

        // 3. Filtro por Municipio
        if (filterMunicipio) {
            result = result.filter(c => c.paciente_municipio === filterMunicipio);
        }

        // 4. Filtro por Parásito detectado
        if (filterParasito) {
            result = result.filter(c =>
                Array.isArray(c.parasitos_detectados) &&
                c.parasitos_detectados.includes(filterParasito)
            );
        }

        // 5. Filtro por Nivel de Riesgo
        if (riskFilter !== "all") {
            result = result.filter(c => c.nivel_peligrosidad === riskFilter);
        }

        // 6. Filtro por Origen (Laboratorio vs Investigación)
        if (originFilter !== "all") {
            result = result.filter(c => {
                const isResearch = c.codigo_proyecto_investigacion && c.codigo_proyecto_investigacion !== "";
                return originFilter === "investigacion" ? isResearch : !isResearch;
            });
        }

        // 7. Ordenamiento alfabético por nombre del paciente
        result.sort((a, b) => {
            const nameA = (a.paciente_nombre || "").toLowerCase();
            const nameB = (b.paciente_nombre || "").toLowerCase();
            if (sortOrder === "asc") return nameA.localeCompare(nameB);
            return nameB.localeCompare(nameA);
        });

        return result;
    }, [casos, filter, labFilter, filterMunicipio, filterParasito, riskFilter, originFilter, sortOrder]);

    // Resumen estadístico para los indicadores superiores
    const stats = {
        total: casos.length,
        alto: casos.filter(c => c.nivel_peligrosidad === "ALTO").length,
        medio: casos.filter(c => c.nivel_peligrosidad === "MEDIO").length,
        bajo: casos.filter(c => c.nivel_peligrosidad === "BAJO").length,
    };

    useEffect(() => {
        const token = localStorage.getItem("token");
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        setCurrentUserRole(userData.role || "");

        if (!token) {
            showToast("⚠️ Sesión no iniciada. Redirigiendo al inicio de sesión...", "warning");
            setTimeout(() => window.location.href = "/Login", 1500);
            setLoading(false);
            return;
        }

        const config = { headers: { Authorization: `Bearer ${token}` } };

        // Carga de todos los casos clínicos del sistema
        axios.get(`${Apiurl}casos/`, config)
            .then(res => {
                setCasos(res.data);
                // Extracción de laboratorios únicos para los filtros de búsqueda
                const uniqueLabs = [...new Set(res.data.map(c => c.laboratorio_nombre))].filter(Boolean);
                setLaboratorios(uniqueLabs);
                setLoading(false);
                if (res.data.length === 0) {
                    showToast("ℹ️ No hay casos registrados en el sistema.", "info");
                }
            })
            .catch(err => {
                console.error("Error al cargar casos:", err);
                setLoading(false);
                if (err.response?.status === 401) {
                    showToast("❌ Sesión expirada. Por favor, inicie sesión nuevamente.", "danger");
                    localStorage.clear();
                    setTimeout(() => window.location.href = "/Login", 2000);
                } else if (err.response?.status === 403) {
                    showToast("❌ No tiene permisos para acceder a esta información.", "danger");
                } else {
                    const errorMsg = err.response?.data?.detail || "Error al cargar los casos. Verifique su conexión.";
                    showToast(`❌ ${errorMsg}`, "danger");
                }
            });
    }, []);

    /**
     * Helper para mostrar mensajes de retroalimentación (Toast Notifications).
     */
    const showToast = (message, type = "success") => {
        const toastEl = toastRef.current;
        if (toastEl) {
            const bsToast = new bootstrap.Toast(toastEl);
            const toastBody = toastEl.querySelector(".toast-body");
            toastBody.textContent = message;
            toastEl.className = `toast align-items-center text-bg-${type} border`;
            bsToast.show();
        }
    };

    const [showExportMenu, setShowExportMenu] = useState(false);

    return (
        <AdminLayout
            breadcrumb="Panel de Administración / Gestión de Casos"
            title="Monitoreo de Casos Epidemiológicos"
        >
            {/* Contenedor de Notificaciones */}
            <div className="toast-container position-fixed top-0 end-0 p-3" style={{ zIndex: 1055 }}>
                <div ref={toastRef} className="toast" role="alert" aria-live="assertive" aria-atomic="true">
                    <div className="d-flex">
                        <div className="toast-body fw-semibold"></div>
                        <button type="button" className="btn-close me-2 m-auto" data-bs-dismiss="toast" aria-label="Cerrar"></button>
                    </div>
                </div>
            </div>

            {/* Tarjetas de Estadísticas Principales */}
            <div className="row g-3 mb-4">
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-body d-flex align-items-center">
                            <i className="bi bi-clipboard-data fs-2 text-primary me-3"></i>
                            <div>
                                <h4 className="m-0 fw-bold">{stats.total}</h4>
                                <small className="text-muted">Casos Totales</small>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-body d-flex align-items-center">
                            <i className="bi bi-bug-fill fs-2 text-danger me-3"></i>
                            <div>
                                <h4 className="m-0 fw-bold">{stats.alto}</h4>
                                <small className="text-muted">Riesgo Alto</small>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-body d-flex align-items-center">
                            <i className="bi bi-bug-fill fs-2 text-warning me-3"></i>
                            <div>
                                <h4 className="m-0 fw-bold">{stats.medio}</h4>
                                <small className="text-muted">Riesgo Medio</small>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-body d-flex align-items-center">
                            <i className="bi bi-bug-fill fs-2 text-success me-3"></i>
                            <div>
                                <h4 className="m-0 fw-bold">{stats.bajo}</h4>
                                <small className="text-muted">Riesgo Bajo</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Panel de Herramientas: Exportación y Múltiples Filtros */}
            <div className="toolbar flex-wrap gap-3">
                <div className="d-flex align-items-center">
                    <div className="dropdown">
                        <button
                            className="btn btn-dark dropdown-toggle py-2 px-3 fw-semibold"
                            type="button"
                            onClick={() => setShowExportMenu(!showExportMenu)}
                        >
                            <i className="bi bi-download me-2"></i> Exportar
                        </button>
                        <ul className={`dropdown-menu border ${showExportMenu ? 'show' : ''}`} style={{ display: showExportMenu ? 'block' : 'none' }}>
                            <li><button className="dropdown-item py-2" onClick={() => { handleExport('xlsx'); setShowExportMenu(false); }}><i className="bi bi-file-earmark-excel-fill me-2 text-success"></i> Excel (.xlsx)</button></li>
                            <li><button className="dropdown-item py-2" onClick={() => { handleExport('csv'); setShowExportMenu(false); }}><i className="bi bi-filetype-csv me-2 text-primary"></i> CSV (Planilla)</button></li>
                            <li><button className="dropdown-item py-2" onClick={() => { handleExport('json'); setShowExportMenu(false); }}><i className="bi bi-filetype-json me-2 text-warning"></i> JSON</button></li>
                            <li><button className="dropdown-item py-2" onClick={() => { handleExport('geojson'); setShowExportMenu(false); }}><i className="bi bi-geo-alt-fill me-2 text-info"></i> GeoJSON</button></li>
                            <li><button className="dropdown-item py-2" onClick={() => { handleExport('pdf'); setShowExportMenu(false); }}><i className="bi bi-file-pdf-fill me-2 text-danger"></i> Reporte PDF</button></li>
                        </ul>
                    </div>
                </div>

                <div className="search-wrapper flex-grow-1" style={{ minWidth: '200px' }}>
                    <i className="bi bi-search"></i>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Buscar paciente, municipio..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </div>

                <div className="d-flex gap-2 flex-wrap align-items-center">
                    {currentUserRole === "admin" && (
                        <select
                            className="form-select"
                            style={{ width: 'auto' }}
                            value={originFilter}
                            onChange={(e) => setOriginFilter(e.target.value)}
                        >
                            <option value="all">Todo Origen</option>
                            <option value="laboratorio">Laboratorio</option>
                            <option value="investigacion">Investigación</option>
                        </select>
                    )}

                    <select
                        className="form-select"
                        style={{ width: 'auto', minWidth: '150px' }}
                        value={labFilter}
                        onChange={(e) => setLabFilter(e.target.value)}
                    >
                        <option value="all">Todos los Labs</option>
                        {laboratorios.map((lab, idx) => (
                            <option key={idx} value={lab}>{lab}</option>
                        ))}
                    </select>

                    <select
                        className="form-select"
                        style={{ width: 'auto' }}
                        value={filterMunicipio}
                        onChange={(e) => setFilterMunicipio(e.target.value)}
                    >
                        <option value="">Todas las Ubicaciones</option>
                        {[...new Set(casos.map(c => c.paciente_municipio))].filter(Boolean).sort().map(m => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>

                    <select
                        className="form-select"
                        style={{ width: 'auto' }}
                        value={filterParasito}
                        onChange={(e) => setFilterParasito(e.target.value)}
                    >
                        <option value="">Todos los Parásitos</option>
                        {[...new Set(casos.flatMap(c => c.parasitos_detectados || []))].filter(Boolean).sort().map(p => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>

                    <select
                        className="form-select"
                        style={{ width: 'auto' }}
                        value={riskFilter}
                        onChange={(e) => setRiskFilter(e.target.value)}
                    >
                        <option value="all">Todo Riesgo</option>
                        <option value="ALTO">Riesgo Alto</option>
                        <option value="MEDIO">Riesgo Medio</option>
                        <option value="BAJO">Riesgo Bajo</option>
                    </select>

                    <select
                        className="form-select"
                        style={{ width: 'auto' }}
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                    >
                        <option value="asc">A-Z (Nombre)</option>
                        <option value="desc">Z-A (Nombre)</option>
                    </select>
                </div>
            </div>

            {/* Tabla de Resultados Epidemiológicos */}
            <div className="table-container">
                <div className="table-responsive">
                    <table className="table admin-table align-middle">
                        <thead>
                            <tr>
                                <th>Paciente / Ubicación</th>
                                <th>Fecha Registro</th>
                                <th>Riesgo / Laboratorio</th>
                                <th>Parásitos Detectados</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status"></div>
                                        <p className="mt-2 text-muted">Cargando registros...</p>
                                    </td>
                                </tr>
                            ) : filteredCasos.length > 0 ? (
                                filteredCasos.map((c) => (
                                    <tr key={c.id}>
                                        <td>
                                            <span className="lab-name">{c.paciente_nombre}</span>
                                            <span className="lab-email">
                                                <i className="bi bi-geo-alt-fill me-1"></i>
                                                {c.paciente_municipio}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="badge bg-light text-dark border">
                                                {(() => {
                                                    if (!c.fecha_registro) return "N/A";
                                                    const [y, m, d] = c.fecha_registro.split('-');
                                                    return `${d}/${m}/${y}`;
                                                })()}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge ${c.nivel_peligrosidad === 'ALTO' ? 'bg-danger' :
                                                c.nivel_peligrosidad === 'MEDIO' ? 'bg-warning text-dark' : 'bg-success'
                                                } mb-1 d-block`} style={{ width: 'fit-content' }}>
                                                {c.nivel_peligrosidad}
                                                {c.algoritmo_detail?.final_score !== undefined && ` (${c.algoritmo_detail.final_score}/100)`}
                                            </span>
                                            <span className="text-muted small d-flex align-items-center">
                                                <i className="bi bi-hospital me-1"></i>
                                                {c.laboratorio_nombre || 'N/A'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {Array.isArray(c.parasitos_detectados) ? (
                                                    c.parasitos_detectados.map((p, idx) => (
                                                        <span key={idx} className="badge bg-info text-dark" style={{ fontSize: '0.7rem' }}>
                                                            {p}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-muted small italic">Ninguno</span>
                                                )}
                                                {Array.isArray(c.parasitos_detectados) && c.parasitos_detectados.length === 0 && (
                                                    <span className="text-muted small italic">Ninguno</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="action-btns">
                                                <button className="action-btn btn-edit" title="Ver Detalles">
                                                    <i className="bi bi-eye-fill"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center py-5 text-muted">
                                        No se encontraron registros de casos.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminCases;
